import { useEffect } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import testIds from "constants/testIds";
import usePrevValue from "hooks/usePrevValue";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import Button from "components/v2/Button/Button";
import { StopAddressType, StopTimeType, StopType } from "types/MultiStop";
import StopAddressFormSection from "./StopAddressFormSection/StopAddressFormSection";
import StopPlanningFormSection from "./StopPlanningFormSection/StopPlanningFormSection";
import StopContactFormSection from "./StopContactFormSection/StopContactFormSection";
import StopTypeFormSection from "./StopTypeFormSection/StopTypeFormSection";
import FormHr from "../../Common/FormHr";
import "./MultiStopForm.scss";
import useGetMultiStopAddressSelect from "../hooks/useGetMultiStopAddressSelect";

interface StopValue {
  address_id?: number;

  stop_key: string; // unique id for frontend form management

  stop_type: StopType; // PICKUP = 1 DROPOFF = 2
  stop_time_type: StopTimeType; // BEFORE = 1, AFTER = 2, BETWEEN = 3
  stop_address_type: StopAddressType; // ADDRESS = 1, AIRPORT = 2, SEAPORT = 3

  stop_airport_name?: string;
  stop_airport_code?: string;
  stop_seaport_name?: string;
  stop_seaport_code?: string;

  stop_contact_name?: string | null;
  stop_contact_number?: string | null;
  stop_contact_email?: string | null;

  stop_after_date?: string | null; // always present for BEFORE, AFTER and BETWEEN (in timezone of the address)
  stop_after_time?: string | null; // always present for BEFORE, AFTER and BETWEEN (in timezone of the address)
  stop_before_date?: string | null; //  present in BETWEEN (in timezone of the address)
  stop_before_time?: string | null; //  present in BETWEEN (in timezone of the address)
  stop_notes?: string;
  stop_hours_start?: string | null;
  stop_hours_end?: string | null;
  stop_timezone?: string | null;
}

const getDefaultStopValue = (): StopValue => {
  const uniqueId = crypto.randomUUID();
  return {
    stop_key: uniqueId,

    stop_type: StopType.DROPOFF, // PICKUP = 1 DROPOFF = 2
    stop_time_type: StopTimeType.BEFORE, // BEFORE = 1, AFTER = 2, BETWEEN = 3
    stop_address_type: StopAddressType.ADDRESS, // ADDRESS = 1, AIRPORT = 2, SEAPORT = 3

    stop_airport_name: "",
    stop_airport_code: "",
    stop_seaport_name: "",
    stop_seaport_code: "",

    stop_contact_name: null,
    stop_contact_number: null,
    stop_contact_email: null,

    stop_after_date: null,
    stop_after_time: null,
    stop_before_date: null,
    stop_before_time: null,
    stop_notes: "",
    stop_hours_start: null,
    stop_hours_end: null,
    stop_timezone: null,
  };
};

const getDefaultPickupStopValue = (): StopValue => ({
  ...getDefaultStopValue(),
  stop_type: StopType.PICKUP,
  stop_time_type: StopTimeType.AFTER,
  stop_after_date: null,
  stop_after_time: null,
});

const getDefaultDeliveryStopValue = (): StopValue => ({
  ...getDefaultStopValue(),
  stop_type: StopType.DROPOFF,
  stop_time_type: StopTimeType.BEFORE,
  stop_before_date: null,
  stop_before_time: null,
});

export default function MultiStopForm({ contactBook, formState }: { contactBook?: any[]; formState: RequestFormState }) {
  const intl = useIntl();
  const { control, watch, setValue, register, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "schedule.stops",
  });
  const { handleSelectAddress } = useGetMultiStopAddressSelect();

  const customField1 = useWatch({ name: `organizational.custom_field_01`, control });
  const prevCustomField1 = usePrevValue(customField1);

  const numberOfStops = watch("schedule.stops")?.length || 0;

  useEffect(() => {
    if (fields.length >= 2) return;
    append([
      { ...getDefaultPickupStopValue(), stop_after_time: "09:00:00", stop_before_time: "09:00:00" },
      { ...getDefaultDeliveryStopValue(), stop_before_time: "17:00:00", stop_after_time: "17:00:00" },
    ]);
  }, [fields.length, append]);

  useEffect(() => {
    if (numberOfStops <= 2) {
      // For 2 stops, set second stop as DROPOFF
      setValue("schedule.stops.1.stop_type", StopType.DROPOFF);
    }
  }, [numberOfStops, setValue]);

  /**
   * if value of customField1 changes from dropdown
   * check if pickup or delivery address is available and prefill it.
   */
  useEffect(() => {
    if (!customField1 || prevCustomField1 === customField1) return;

    const dropdownValues = formState.locationDetails?.organizational_options?.custom_field_01?.dropdown_values;
    if (!Array.isArray(dropdownValues)) return;

    const selectedOption = dropdownValues.find((item: any) => item.value === customField1);
    const stops = getValues("schedule.stops") || [];

    const normalizeAddress = (address: any) => ({
      ...address,
      ...(address?.city?.id && { city: address.city.id }),
      ...(address?.country?.id && { country: address.country.id }),
      ...(address?.state?.id && { state: address.state.id }),
    });

    const prefillAddress = (rawAddress: any, stopType: StopType) => {
      if (!rawAddress) return;
      const stopIndex = stops.findIndex((stop: any) => stop.stop_type === stopType);
      if (stopIndex !== -1) {
        handleSelectAddress([normalizeAddress(rawAddress)], stopIndex);
      }
    };

    prefillAddress(selectedOption?.pickup_address, StopType.PICKUP);
    prefillAddress(selectedOption?.delivery_address, StopType.DROPOFF);
  }, [customField1, prevCustomField1]);

  const handleRemoveStop = (index: number) => {
    if (fields.length <= 2) return;
    remove(index);
    // WEB-11639: classic pickup + delivery must not keep packing-list stop keys.
    if (fields.length - 1 <= 2) {
      const packingList = getValues("packing_list") || [];
      packingList.forEach((_: unknown, packingIndex: number) => {
        setValue(`packing_list.${packingIndex}.pickup_stop_key`, null, { shouldDirty: true });
        setValue(`packing_list.${packingIndex}.delivery_stop_key`, null, { shouldDirty: true });
      });
    }
  };

  return (
    <div className="mt-3xl">
      {fields.map((field, index) => (
        <div key={field.id} data-testid={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_CONTAINER_PREFIX}-${index}`}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="mb-lg text-secondary-v2 text-lg font-medium">
              {numberOfStops <= 2 ? (
                <>
                  {watch(`schedule.stops.${index}.stop_type`) === StopType.PICKUP ? (
                    <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
                  ) : (
                    <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
                  )}
                </>
              ) : (
                <FormattedMessage
                  id="new-request.schedule.multistop.stopNumber"
                  defaultMessage="Stop #{stopNumber}"
                  values={{ stopNumber: index + 1 }}
                />
              )}
            </div>
            {index > 0 && numberOfStops > 2 && (
              <Button
                iconLeft={<TrashIcon />}
                variant="link-error"
                className="pb-lg"
                onClick={() => handleRemoveStop(index)}
              />
            )}
          </div>

          <StopTypeFormSection index={index} />
          <StopAddressFormSection index={index} />
          <StopPlanningFormSection index={index} />
          <StopContactFormSection index={index} contactBook={contactBook} />
          {index < fields.length - 1 && <FormHr />}
        </div>
      ))}
      <Button
        label={intl.formatMessage({
          id: "new-request.schedule.multistop.addStop",
          defaultMessage: "Add stop",
        })}
        iconLeft={<PlusIcon />}
        className="mb-3xl"
        size="sm"
        data-testid={testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_ADD_STOP_BUTTON}
        onClick={() => {
          append(getDefaultPickupStopValue());
          // Blur any focused element after append
          setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }, 0);
        }}
      />
      {numberOfStops > 2 && (
        <div>
          <div className="text-secondary-v2 text-md font-medium mb-sm">
            <FormattedMessage
              id="new-request.schedule.multistop.multistopInstructions"
              defaultMessage="Multi-stop instructions"
            />
          </div>
          <textarea
            id="multistop-instructions"
            data-testid={testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_INSTRUCTIONS}
            {...register("schedule.multistop_instructions", { shouldUnregister: true })}
            placeholder={intl.formatMessage({
              id: "new-request.schedule.multistop.enterText",
              defaultMessage: "Enter text",
            })}
            className="multistop-instructions-textarea"
          />
        </div>
      )}
    </div>
  );
}

MultiStopForm.defaultProps = {
  contactBook: [],
};
